import { DEFAULT_AVATARS_GROUPING, DEFAULT_TOP_AVATARS } from '@/pages/agents/constants';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useFlag } from '@/services/featureFlags';
import { Presenter, PresenterUsecase } from '@d-id/types';
import { useCallback, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';

export interface AvatarGroup {
    id: string;
    mainAvatar: Presenter;
    alternatives: Presenter[];
}

export interface OrderItem {
    type: 'avatar' | 'group';
    avatar?: Presenter;
    group?: AvatarGroup;
}

export const useAvatarOrdering = () => {
    const { publicAvatars, isLoading } = useAllAvatars(PresenterUsecase.AgentV2);
    const topAvatars: string[] = useFlag('top_avatars', DEFAULT_TOP_AVATARS);
    const avatarsGrouping: Record<string, string[]> = useFlag('avatars_grouping', DEFAULT_AVATARS_GROUPING);

    const [orderedItems, setOrderedItems] = useState<OrderItem[]>([]);

    const buildOrderedItems = useCallback(() => {
        if (publicAvatars.length === 0) return [];

        const items: OrderItem[] = [];
        const groupedAvatarIds = new Set(Object.values(avatarsGrouping).flat());
        const mainAvatarIds = new Set(Object.keys(avatarsGrouping));

        Object.entries(avatarsGrouping).forEach(([mainId, altIds]) => {
            const mainAvatar = publicAvatars.find(avatar => avatar.id === mainId);
            const alternatives = altIds.map(id => publicAvatars.find(avatar => avatar.id === id)).filter(Boolean) as Presenter[];

            if (mainAvatar) {
                items.push({
                    type: 'group',
                    group: {
                        id: mainId,
                        mainAvatar,
                        alternatives,
                    },
                });
            }
        });

        const ungroupedAvatars = publicAvatars.filter(avatar => !groupedAvatarIds.has(avatar.id) && !mainAvatarIds.has(avatar.id));

        ungroupedAvatars.forEach(avatar => {
            items.push({
                type: 'avatar',
                avatar,
            });
        });

        // Sort based on top_avatars order, putting unordered items at the end
        return items.sort((a, b) => {
            const aId = a.type === 'avatar' ? a.avatar!.id : a.group!.mainAvatar.id;
            const bId = b.type === 'avatar' ? b.avatar!.id : b.group!.mainAvatar.id;

            const aIndex = topAvatars.indexOf(aId);
            const bIndex = topAvatars.indexOf(bId);

            if (aIndex === -1 && bIndex === -1) return aId.localeCompare(bId);
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [publicAvatars, topAvatars, avatarsGrouping]);

    const handleDragEnd = useCallback(
        (result: DropResult, handleDragEndCleanup: () => void) => {
            handleDragEndCleanup();

            try {
                if (!result.destination) {
                    return;
                }

                const { source, destination } = result;

                if (source.droppableId === 'ordered-items' && destination.droppableId === 'ordered-items') {
                    if (source.index === destination.index) return;

                    const newItems = [...orderedItems];
                    const [reorderedItem] = newItems.splice(source.index, 1);
                    newItems.splice(destination.index, 0, reorderedItem);
                    setOrderedItems(newItems);
                } else if (destination.droppableId.startsWith('group-')) {
                    const groupId = destination.droppableId.replace('group-', '');
                    const avatarId = result.draggableId;

                    const draggedAvatar = orderedItems.find(item => item.type === 'avatar' && item.avatar!.id === avatarId)?.avatar;

                    if (draggedAvatar) {
                        const newItems = orderedItems
                            .filter(item => !(item.type === 'avatar' && item.avatar!.id === avatarId)) // Remove from main list
                            .map(item => {
                                if (item.type === 'group' && item.group!.id === groupId) {
                                    return {
                                        ...item,
                                        group: {
                                            ...item.group!,
                                            alternatives: [...item.group!.alternatives, draggedAvatar],
                                        },
                                    };
                                }
                                return item;
                            });
                        setOrderedItems(newItems);
                    }
                }
            } catch (error) {
                console.error('Drag error:', error);
            }
        },
        [orderedItems]
    );

    const createGroup = useCallback(
        (mainAvatar: Presenter) => {
            const newItems = orderedItems.map(item => {
                if (item.type === 'avatar' && item.avatar!.id === mainAvatar.id) {
                    return {
                        type: 'group' as const,
                        group: {
                            id: mainAvatar.id,
                            mainAvatar,
                            alternatives: [],
                        },
                    };
                }
                return item;
            });
            setOrderedItems(newItems);
        },
        [orderedItems]
    );

    const ungroupAvatar = useCallback(
        (groupId: string) => {
            const newItems: OrderItem[] = [];

            orderedItems.forEach(item => {
                if (item.type === 'group' && item.group!.id === groupId) {
                    newItems.push({ type: 'avatar', avatar: item.group!.mainAvatar });
                    item.group!.alternatives.forEach(alt => {
                        newItems.push({ type: 'avatar', avatar: alt });
                    });
                } else {
                    newItems.push(item);
                }
            });

            setOrderedItems(newItems);
        },
        [orderedItems]
    );

    const removeFromGroup = useCallback(
        (groupId: string, avatarId: string) => {
            const newItems = orderedItems.map(item => {
                if (item.type === 'group' && item.group!.id === groupId) {
                    const updatedAlternatives = item.group!.alternatives.filter(alt => alt.id !== avatarId);
                    return {
                        ...item,
                        group: {
                            ...item.group!,
                            alternatives: updatedAlternatives,
                        },
                    };
                }
                return item;
            });
            setOrderedItems(newItems);
        },
        [orderedItems]
    );

    const resetToDefaults = useCallback(() => {
        const items = buildOrderedItems();
        setOrderedItems(items);
    }, [buildOrderedItems]);

    return {
        publicAvatars,
        isLoading,
        orderedItems,
        setOrderedItems,
        buildOrderedItems,
        handleDragEnd,
        createGroup,
        ungroupAvatar,
        removeFromGroup,
        resetToDefaults,
    };
};
